import DateRangeSlicer from '@/library/DateRangeSlicer';
import stravaApi from '@/domain/StravaApi';
import sleep from '@/library/sleep';
import cache from '@/cache/indexedDB/indexedDbCache';
import logger from '@/domain/ClientLogger';
import store from '@/store';
import moment from 'moment';
import linq from '@/library/linq';
import { IActivity } from '@/domain/StravaTypes';

const startDate = new Date(2013, 0, 1);
// const startDate = new Date(2018, 0, 1);

class DataAccessor {
    private daysInSlice: number;

    constructor(daysInSlice: number) {
        this.daysInSlice = daysInSlice;
    }

    public async getSegment(id: number) {
        let segment = await cache.getSegment(id);
        if (segment === undefined) {
            segment = await stravaApi.getSegment(id);
            await cache.setSegment(id, segment);
        }
        return segment;
    }

    public async getActivity(id: number) {
        let activity: IActivity = await cache.getActivity(id);
        if (activity === undefined) {
            activity = await stravaApi.getActivity(id);
        }
        if (activity) {
            await cache.setActivity(id, activity);
            const segments = linq.distinct(activity.segment_efforts.map((se) => se.segment), 'id');
            segments.forEach(async (s) => {
                await cache.setSegment(s.id, s);
            });
        }
        return activity;
    }

    public async getActivityStreams(id: number) {
        let stream = await cache.getActivityStreams(id);
        if (stream === undefined) {
            stream = await stravaApi.getActivityStreams(id);
            await cache.setActivityStreams(id, stream);
        }
        return stream;
    }

    public async getSegmentStreams(id: number) {
        let stream = await cache.getSegmentStreams(id);
        if (stream === undefined) {
            stream = await stravaApi.getSegmentStreams(id);
            await cache.setSegmentStreams(id, stream);
        }
        return stream;
    }

    public async sync(feedback: (current: number, total: number, activities: any) => void, delay: number = 0) {
        return this.syncFrom(startDate, feedback, delay);
    }

    public async syncFrom(from: Date, feedback: (current: number, total: number, activities: any) => void, delay: number = 0) {
        const slicer = new DateRangeSlicer();
        const slices = slicer.slice(from, new Date(Date.now()), this.daysInSlice);
        // reverse the date range slices to load the most recent first
        slices.sort((a, b) => (+b.start / 1000) - (+a.start / 1000));
        let activities = [];
        let loaded = 0;
        let lastestActivity = undefined;
        const all = await cache.getAllActivitySummaries();
        if (all !== undefined) {
            const startDates = linq.selectMany(all, (a) => a).map((a) => moment(a.start_date).unix());
            lastestActivity = startDates.length > 0 ? Math.max(...startDates) : undefined;
        }
        for (const slice of slices) {
            const loggedInId = store.state.athlete.id;
            const key = this.getActivitySummaryKey(loggedInId, slice.start, slice.end);
            let acts = await cache.getActivitySummaries(key);
            if ((acts === undefined) || (lastestActivity === undefined) || (moment(slice.end).unix() > lastestActivity)) {
                const reason = acts === undefined ? 'not found' : 'refresh';
                logger.log(`key[${key}] - ${reason}`);
                try {
                    acts = await stravaApi.getLoggedInAthleteActivities(slice.start, slice.end, 1, 100);
                    await cache.setActivitySummaries(key, acts);
                } catch (err) {
                    logger.log(`key[${key}] - error - ${err}`);
                }
            } else {
                logger.log(`key[${key}] - Found!`);
            }
            activities = activities.concat(acts);
            feedback(++loaded, slices.length, activities);
            if (delay > 0) {
                await sleep(delay);
            }
        }
        activities.sort((a, b) => a.start_date - b.start_date);
        return activities;
    }

    private getActivitySummaryKey(id: number, start: Date, end: Date): string {
        const s = moment(start).format('YYYYMMDD');
        const e = moment(end).format('YYYYMMDD');
        return `${id}:${s}:${e}`;
    }
}

const da = new DataAccessor(30);

export default da;
